"use client";

import { useState, useEffect } from "react";
import { Facebook, Twitter, Instagram, Book, HelpCircle, MessageSquareQuote, Bug, Users, User, Code, Info, Mail, Shield, Wrench, FileText, Building2 } from "lucide-react";
import Link from "next/link";
import { Separator } from "@/components/ui/separator";

// Custom TikTok icon component
const TikTokIcon = ({ size = 20 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const SOCIAL_LINKS = [
  { href: "https://facebook.com/tryrentwise", icon: Facebook, label: "Follow RentWise on Facebook" },
  { href: "https://x.com/tryrentwise", icon: Twitter, label: "Follow RentWise on X (Twitter)" },
  { href: "https://instagram.com/tryrentwise", icon: Instagram, label: "Follow RentWise on Instagram" },
  { href: "https://tiktok.com/tryrentwise", icon: TikTokIcon, label: "Follow RentWise on TikTok" },
] as const;

const NAVIGATION_SECTIONS = [
  {
    title: "Let's Grow",
    links: [
      { href: "/bug-bounty", icon: Bug, text: "Bug Bounty" },
      { href: "/feature-requests", icon: Wrench, text: "Feature Requests" },
      { href: "/beta-program", icon: Code, text: "Beta Program" },
    ]
  },
  {
    title: "Products", 
    links: [
      { href: "/for-tenants", icon: Users, text: "For Tenants" },
      { href: "/for-landlords", icon: User, text: "For Landlords" },
      { href: "/developers", icon: Building2, text: "JV & Estate Developers" },
    ]
  },
  {
    title: "Resources",
    links: [
      { href: "/blog", icon: Book, text: "RentWise Blog" },
      { href: "/help", icon: HelpCircle, text: "Help Center" },
      { href: "/faq", icon: MessageSquareQuote, text: "FAQ" },
      { href: "/vulnerability", icon: Bug, text: "Report a Vulnerability" },
    ]
  },
  {
    title: "Company",
    links: [
      { href: "/pricing", icon: FileText, text: "Pricing" },
      { href: "/about", icon: Info, text: "About Us" },
      { href: "/contact", icon: Mail, text: "Contact Us" },
      { href: "/privacy", icon: Shield, text: "Privacy & Policy" },
    ]
  }
] as const;

const FOOTER_LINKS = [
  { href: "/ads", text: "Ads" },
  { href: "/terms", text: "Terms" },
  { href: "/llm", text: "LLMs" },
  { href: "/cookies", text: "Cookies" },
] as const;

export default function Footer() {
  // ✅ 2025 SSR FIX: Client-only year to prevent hydration mismatch during timezone boundaries
  const [currentYear, setCurrentYear] = useState('')
  
  useEffect(() => {
    // Set current year only on client to avoid SSR hydration mismatch
    setCurrentYear(new Date().getFullYear().toString())
  }, [])

  return (
    <footer className="py-6 sm:py-8 md:py-12 bg-white border-t border-gray-200">
      <div className="container mx-auto px-4">
        {/* Company Summary - Full width */}
        <div className="mb-10">
          <h3 className="text-xl font-bold text-gray-900 mb-4">RentWise</h3>
          <p className="text-gray-600 mb-6 max-w-lg leading-relaxed">
            We're building a smarter renting future by creating transparency in the rental market. Our platform helps tenants find trustworthy homes and landlords showcase quality properties.
          </p>
          <div className="flex gap-4">
            {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                aria-label={label}
              >
                <Icon size={20} />
              </a>
            ))}
          </div>
        </div>
        
        {/* Four sections in a responsive grid with consistent heights */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
          {NAVIGATION_SECTIONS.map(({ title, links }) => (
            <div key={title} className="min-h-[180px]">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 h-7">{title}</h3>
              <ul className="space-y-3">
                {links.map(({ href, icon: Icon, text }) => (
                  <li key={href}>
                    <Link 
                      href={href}
                      className="text-gray-600 hover:text-blue-600 transition-colors duration-200 flex items-center gap-2"
                    >
                      <Icon size={16} />
                      <span>{text}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <Separator className="my-6" />
        
        {/* Bottom section with fixed height to prevent resizing */}
        <div className="bg-purple-50 px-4 py-4 rounded-lg min-h-[60px]">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500 order-2 sm:order-1">
              © {currentYear} RentWise. All rights reserved.
            </div>
            
            <div className="flex flex-wrap gap-4 justify-center sm:justify-end order-1 sm:order-2">
              {FOOTER_LINKS.map(({ href, text }) => (
                <Link
                  key={href}
                  href={href}
                  className="text-sm text-purple-600 hover:text-purple-400 transition-colors duration-200 px-3 py-1 rounded-md bg-white shadow-sm whitespace-nowrap"
                >
                  {text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
